import React from "react";
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    "& .MuiTableCell-root": {
      borderBottom: "1px solid #333",
      color: "#fff",
    },
    "& .MuiTableHead-root": {
      backgroundColor: "#2a2a2a",
    },
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const CookiePolicy: React.FC = () => {
  const classes = useStyles();

  return (
    <Container maxWidth="md" className={classes.container}>
      <Typography variant="h3" className={classes.title}>
        Cookie Policy
      </Typography>

      <Typography variant="body2" className={classes.lastUpdated}>
        Last updated: October 17, 2025
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        1. Introduction
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        This Cookie Policy explains how SugarCribs.com ("Sugar Cribs," "we,"
        "us," or "our") uses cookies and similar technologies to recognize you
        when you visit our website and use our services. It describes what these
        technologies are, why we use them, and your rights to control their use.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        This policy should be read together with our Privacy Policy, which
        describes how we collect, use, and protect personal data.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        By continuing to browse or use our services, you agree to the use of
        cookies as described below unless you choose to disable them through
        your browser or our cookie settings.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        2. What Are Cookies?
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Cookies are small text files placed on your device when you visit a
        website. They help us make your experience more efficient by remembering
        preferences, login details, and providing insight into how our platform
        is used.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Cookies can be:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          <strong>Session cookies:</strong> Expire when you close your browser.
        </li>
        <li className={classes.listItem}>
          <strong>Persistent cookies:</strong> Remain on your device for a
          defined period.
        </li>
        <li className={classes.listItem}>
          <strong>First-party cookies:</strong> Set directly by SugarCribs.com.
        </li>
        <li className={classes.listItem}>
          <strong>Third-party cookies:</strong> Set by external providers (e.g.,
          Google Analytics, CCBill).
        </li>
      </Box>

      <Typography variant="h5" className={classes.subtitle}>
        3. Why We Use Cookies
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We use cookies and similar technologies to:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Authenticate users and maintain sessions securely.
        </li>
        <li className={classes.listItem}>
          Remember preferences, such as region and language.
        </li>
        <li className={classes.listItem}>
          Analyze site usage through tools like Firebase Analytics and Google
          Analytics.
        </li>
        <li className={classes.listItem}>
          Prevent fraud and ensure payment and age verification integrity (e.g.,
          CCBill).
        </li>
        <li className={classes.listItem}>
          Improve performance and user experience.
        </li>
      </Box>

      <Typography variant="h5" className={classes.subtitle}>
        4. Types of Cookies We Use
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              <TableCell>
                <strong>Purpose</strong>
              </TableCell>
              <TableCell>
                <strong>Examples / Providers</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <strong>Essential Cookies</strong>
              </TableCell>
              <TableCell>
                Required for the site to function — for login, security, and
                navigation. Cannot be disabled.
              </TableCell>
              <TableCell>
                Session ID, Authentication Token, Firebase Session
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Performance & Analytics Cookies</strong>
              </TableCell>
              <TableCell>
                Help us understand how users interact with the site to improve
                usability.
              </TableCell>
              <TableCell>Google Analytics, Firebase Analytics</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Functional Cookies</strong>
              </TableCell>
              <TableCell>
                Enable personalized features such as remembering login states or
                preferences.
              </TableCell>
              <TableCell>Locale, Theme Settings</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Third-Party / Compliance Cookies</strong>
              </TableCell>
              <TableCell>
                Used by third-party services integrated into SugarCribs (e.g.,
                payments, verification).
              </TableCell>
              <TableCell>CCBill, DidIt.me, Cloudflare Security</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Advertising Cookies</strong>
              </TableCell>
              <TableCell>
                Track usage across sites to deliver relevant content or
                promotions.
              </TableCell>
              <TableCell>
                (Currently not used, but may be added in future updates)
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" className={classes.subtitle}>
        5. How to Control Cookies
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        You can manage or disable cookies through your browser settings or
        through our on-site cookie preferences banner (if available).
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Most browsers allow you to:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>View and delete cookies;</li>
        <li className={classes.listItem}>Block cookies from specific sites;</li>
        <li className={classes.listItem}>Block all cookies; or</li>
        <li className={classes.listItem}>
          Delete all cookies when closing your browser.
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        Common links for browser settings:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            Chrome
          </a>
        </li>
        <li className={classes.listItem}>
          <a
            href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            Safari
          </a>
        </li>
        <li className={classes.listItem}>
          <a
            href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            Firefox
          </a>
        </li>
        <li className={classes.listItem}>
          <a
            href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            Edge
          </a>
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        If you disable or reject certain cookies, some parts of the Service may
        not function properly.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        6. Consent for EU and UK Users
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Under the EU ePrivacy Directive and UK PECR, we must obtain your consent
        before placing non-essential cookies (analytics, functional, or
        advertising).
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        When you first visit SugarCribs.com, a cookie consent banner will appear
        allowing you to:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>Accept all cookies;</li>
        <li className={classes.listItem}>Reject non-essential cookies; or</li>
        <li className={classes.listItem}>Manage your preferences.</li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        You can change your consent at any time by visiting Cookie Settings in
        the footer or clearing cookies in your browser.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        7. U.S. State Law Notice (CCPA / CPRA)
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        If you are a resident of California, Virginia, Colorado, or other U.S.
        states with privacy laws, cookies may be considered a "sale" or
        "sharing" of personal information.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        You have the right to opt out of such sharing. You can do so by:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          Adjusting cookie preferences in the banner or settings, or
        </li>
        <li className={classes.listItem}>
          Contacting us at support@sugarcribs.com.
        </li>
      </Box>

      <Typography variant="h5" className={classes.subtitle}>
        8. Third-Party Cookies
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        Some cookies are placed by third parties that provide services to
        SugarCribs. These may include:
      </Typography>
      <Box component="ul" style={{ paddingLeft: "20px" }}>
        <li className={classes.listItem}>
          <strong>Google Analytics</strong> – for anonymous usage analysis.
        </li>
        <li className={classes.listItem}>
          <strong>Firebase</strong> – for authentication and analytics.
        </li>
        <li className={classes.listItem}>
          <strong>CCBill</strong> – for secure payment processing.
        </li>
        <li className={classes.listItem}>
          <strong>DidIt.me</strong> – for identity verification (KYC/AML).
        </li>
      </Box>
      <Typography variant="body1" className={classes.paragraph}>
        These providers may collect data per their own privacy policies. We
        encourage you to review their statements for more details.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        9. Updates to This Cookie Policy
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We may update this Cookie Policy periodically to reflect changes in
        technology, legal requirements, or our business operations. The "Last
        updated" above indicates when the policy was last updated.
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        We encourage you to revisit this page periodically to stay informed
        about our use of cookies and related technologies.
      </Typography>

      <Typography variant="h5" className={classes.subtitle}>
        10. Contact Us
      </Typography>
      <Typography variant="body1" className={classes.paragraph}>
        If you have any questions or concerns about this Cookie Policy or our
        use of cookies, please contact us at:
      </Typography>

      <Box className={classes.contactInfo}>
        <Typography
          variant="h6"
          style={{ color: "#FE5A97", marginBottom: "16px" }}
        >
          Contact Information
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>TOPV1 LLC (d/b/a "Sugar Cribs")</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          📍 732 S. 6th Street, Suite N
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Las Vegas, NV 89101, USA
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          ✉️ support@sugarcribs.com
        </Typography>
      </Box>
    </Container>
  );
};

export default CookiePolicy;
